import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Armchair,
  Info,
  MoreHorizontal,
  RefreshCw,
  Search,
  Unlink,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useStudentsList } from "@/hooks/useStudents";
import { AssignSeatSheet } from "@/components/seating/AssignSeatSheet";
import { ReleaseAssignmentDialog } from "@/components/seating/ReleaseAssignmentDialog";
import {
  useSeatAssignments,
  type SeatAssignment,
  type SeatAssignmentListFilters,
  type SeatAssignmentStatus,
} from "@/lib/seating/allocations";
import { useSeats } from "@/lib/seating/seats";
import { useZones } from "@/lib/seating/zones";
import { useSeatConfig } from "@/lib/seatAssignmentConfig/api";

/**
 * Phase F-finale-7 Slice 7b.4c — IA fixed-mode allocation surface.
 *
 * The permanent-seat allocation view (Iron Rule #16 `<DataTable>`) over the
 * real-auth `/api/seat-assignments` surface: each row is a (member, seat)
 * allocation with its lifecycle status. Admins assign via `<AssignSeatSheet>`
 * and release via `<ReleaseAssignmentDialog>` (reason-required).
 *
 * Fixed allocations are only meaningful when the tenant's
 * `seat_assignment_config.mode === "fixed"` — the BE 400s an assign in any
 * other mode — so when the mode isn't fixed we surface an inline notice
 * linking to the seat-assignment config editor instead of the Assign CTA.
 *
 * Write affordances are gated on `useCanEditSeating` (`manage_zones`),
 * identical to Zones/Seats; the BE re-checks on every call.
 */

const ANY_ZONE = "__any__";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "released", label: "Released" },
  { value: "expired", label: "Expired" },
  { value: "__any__", label: "Any status" },
] as const;

type StatusFilter = (typeof STATUS_FILTER_OPTIONS)[number]["value"];

/** Allocation status → the shared `<StatusPill>` palette + label. */
const STATUS_PILL: Record<string, { status: StatusPillStatus; label: string }> = {
  active: { status: "active", label: "Active" },
  released: { status: "inactive", label: "Released" },
  expired: { status: "archived", label: "Expired" },
};

type DialogState =
  | { kind: "none" }
  | { kind: "assign" }
  | { kind: "release"; assignment: SeatAssignment };

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

export function AllocationsPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();

  const [qInput, setQInput] = React.useState("");
  const [zoneFilter, setZoneFilter] = React.useState<string>(ANY_ZONE);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("active");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "seating-allocations",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  const configQuery = useSeatConfig();
  const isFixedMode = configQuery.data?.mode === "fixed";

  // Zones + seats + students power the filters and the row-level name
  // resolution (assignments carry only ids on the wire).
  const zonesQuery = useZones({ limit: 200 });
  const zones = React.useMemo(() => zonesQuery.data?.items ?? [], [zonesQuery.data]);
  const zoneNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const z of zones) map.set(z.id, z.name);
    return map;
  }, [zones]);

  const seatsQuery = useSeats({ limit: 200 });
  const seatById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of seatsQuery.data?.items ?? []) map.set(s.id, s.seat_number);
    return map;
  }, [seatsQuery.data]);

  const studentsQuery = useStudentsList({ limit: 200 });
  const studentNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of studentsQuery.data?.items ?? []) map.set(s.id, s.name);
    return map;
  }, [studentsQuery.data]);

  const filters = React.useMemo<SeatAssignmentListFilters>(
    () => ({
      zone_id: zoneFilter === ANY_ZONE ? undefined : zoneFilter,
      status: statusFilter === "__any__" ? undefined : (statusFilter as SeatAssignmentStatus),
      limit: pageSize,
      offset: page * pageSize,
    }),
    [zoneFilter, statusFilter, page, pageSize],
  );

  const query = useSeatAssignments(filters);
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const seatLabel = React.useCallback(
    (a: SeatAssignment) => seatById.get(a.seat_id) ?? a.seat_id,
    [seatById],
  );
  const studentLabel = React.useCallback(
    (a: SeatAssignment) => studentNameById.get(a.student_id) ?? a.student_id,
    [studentNameById],
  );

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((a) =>
      [studentLabel(a), seatLabel(a), zoneNameById.get(a.zone_id ?? "") ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allItems, qInput, seatLabel, studentLabel, zoneNameById]);

  const hasFiltersApplied = qInput !== "" || zoneFilter !== ANY_ZONE || statusFilter !== "active";

  const clearFilters = React.useCallback(() => {
    setQInput("");
    setZoneFilter(ANY_ZONE);
    setStatusFilter("active");
    setPage(0);
  }, []);

  const showAssign = canEditSeating && isFixedMode;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Allocations</PageHeaderHeading>
            <PageHeaderDescription>
              Permanent seat assignments. Each member holds one seat that stays theirs across
              sessions.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so allocations are read-only. Ask an administrator to grant it if you need to make
              changes.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!missingRbac && configQuery.isSuccess && !isFixedMode ? (
        <Card data-testid="seating-mode-notice" className="border-sky-300/60">
          <CardHeader>
            <div className="flex items-start gap-2">
              <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-sky-600" />
              <div className="space-y-1">
                <CardTitle className="text-base">Permanent allocations need fixed mode</CardTitle>
                <CardDescription>
                  Your seating mode isn&apos;t set to <span className="font-medium">Fixed</span>, so
                  new permanent allocations can&apos;t be created.{" "}
                  <Link to="/seating/config" className="text-primary font-medium underline">
                    Switch to fixed mode
                  </Link>{" "}
                  to assign permanent seats.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load allocations
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search member or seat…"
              className="pl-8"
              aria-label="Search allocations"
            />
          </div>
          <Select
            value={zoneFilter}
            onValueChange={(v) => {
              setZoneFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Zone filter">
              <SelectValue placeholder="Any zone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_ZONE}>Any zone</SelectItem>
              {zones.map((z) => (
                <SelectItem key={z.id} value={z.id}>
                  {z.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
          {showAssign ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "assign" })}
              data-testid="allocations-assign-button"
            >
              <Armchair aria-hidden="true" className="size-4" />
              Assign seat
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%]">Member</TableHead>
                <TableHead className="w-[26%]">Seat</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[22%]">Assigned</TableHead>
                <TableHead className="w-[8%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={5} />
            ) : (
              <TableBody>
                {filteredItems.map((a) => {
                  const pill = STATUS_PILL[a.status] ?? { status: "inactive", label: a.status };
                  const zoneName = a.zone_id ? zoneNameById.get(a.zone_id) : undefined;
                  return (
                    <TableRow key={a.id} data-testid={`allocation-row-${a.id}`}>
                      <TableCell className="font-medium">{studentLabel(a)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {seatLabel(a)}
                        {zoneName ? (
                          <span className="text-muted-foreground/70"> · {zoneName}</span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={pill.status} label={pill.label} />
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        <span title={a.starts_at}>{formatDate(a.starts_at)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditSeating && a.status === "active" ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${studentLabel(a)}`}
                                data-testid={`allocation-row-actions-${a.id}`}
                                className="size-7"
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setDialog({ kind: "release", assignment: a })}
                                data-testid={`allocation-action-release-${a.id}`}
                              >
                                <Unlink aria-hidden="true" className="size-4" />
                                Release seat
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<Armchair aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : "No allocations yet"}
            description={
              hasFiltersApplied
                ? "Adjust filters or clear them to see every allocation."
                : isFixedMode
                  ? "Assign a permanent seat to a member to get started."
                  : "Permanent allocations appear here once seating is in fixed mode."
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : showAssign ? (
                <Button size="sm" onClick={() => setDialog({ kind: "assign" })}>
                  <Armchair aria-hidden="true" className="size-4" />
                  Assign seat
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="allocations-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canEditSeating ? (
        <>
          <AssignSeatSheet
            open={dialog.kind === "assign"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            zones={zones}
            onSaved={() => setDialog({ kind: "none" })}
          />
          <ReleaseAssignmentDialog
            open={dialog.kind === "release"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            assignment={dialog.kind === "release" ? dialog.assignment : null}
            seatLabel={dialog.kind === "release" ? seatLabel(dialog.assignment) : undefined}
            studentLabel={dialog.kind === "release" ? studentLabel(dialog.assignment) : undefined}
            onReleased={() => setDialog({ kind: "none" })}
          />
        </>
      ) : null}
    </div>
  );
}
